import { AfterViewInit, ChangeDetectionStrategy, Component, inject, resource, ViewChild } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import {
  InputCustomEvent,
  IonButton,
  IonButtons,
  IonCol,
  IonContent,
  IonGrid,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonRow,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { SpinnerLoadingComponent } from '@okcargo-apps/layout-ionic';
import { IStopAddress } from '@okcargo-apps/shared-domain';
import { IAddressBookEntry } from '@okcargo-apps/shared-domain-ionic';
import { AddressbookService } from '../addressbook.service';

@Component({
  selector: 'lib-search-addressbook-modal',
  templateUrl: './search-addressbook-modal.component.html',
  styleUrls: ['./search-addressbook-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    IonLabel,
    IonItem,
    IonContent,
    IonCol,
    IonRow,
    IonGrid,
    IonInput,
    IonTitle,
    IonIcon,
    IonButton,
    IonToolbar,
    IonButtons,
    IonHeader,
    SpinnerLoadingComponent,
  ],
})
export class SearchAddressbookModalComponent implements AfterViewInit {
  private readonly fb = inject(FormBuilder);
  private readonly modalCtrl = inject(ModalController);
  private readonly addressbookService = inject(AddressbookService);

  @ViewChild('searchInput') searchInput!: IonInput;

  public readonly formAddressEdit = this.fb.group({
    searchTerm: ['', Validators.minLength(2)],
  });
  public readonly searchTerm = toSignal(this.formAddressEdit.controls.searchTerm.valueChanges, {
    initialValue: this.formAddressEdit.controls.searchTerm.value ?? '',
  });

  public readonly addressbookResource = resource({
    params: () => this.searchTerm(),
    loader: async ({ params: term, abortSignal }) => {
      // Bloqueamos la petición si el término no es válido
      if (!term || term.trim().length < 2) {
        return [];
      }
      return this.addressbookService.searchAddresses(term, abortSignal);
    },
  });

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.searchInput?.setFocus();
    }, 100);
  }

  public handleChangeTerm(event: InputCustomEvent): void {
    const value = event.detail.value ?? '';
    this.formAddressEdit.controls.searchTerm.setValue(value);
  }

  public async handleSelectAddress(addressbookEntry: IAddressBookEntry): Promise<boolean> {
    if (addressbookEntry.placeId) {
      const company = addressbookEntry.textToDisplay.split(',')[0].trim();
      const newAddressFound = await this.addressbookService.findAddressByPlaceId(addressbookEntry.placeId);

      if (newAddressFound) {
        const newAddress = { ...newAddressFound, company } as IStopAddress;

        if (!this.isValidStopAddress(newAddress)) {
          console.error('Invalid address data received from the backend:', newAddress);
          // TODO decide how to surface this error to the user
          return false;
        }

        return this.modalCtrl.dismiss(newAddress, 'confirm');
      }
    }

    if (addressbookEntry.address) {
      if (!this.isValidStopAddress(addressbookEntry.address)) {
        console.error('Invalid address data received from the backend:', addressbookEntry.address);
        // TODO decide how to surface this error to the user
        return false;
      }

      return this.modalCtrl.dismiss(addressbookEntry.address, 'confirm');
    }

    return false;
  }

  private isValidStopAddress(address: IStopAddress): boolean {
    return [
      address.company,
      address.street,
      address.city,
      address.postalCode,
      address.stateCode,
      address.countryCode,
    ].every((value) => typeof value === 'string' && value.trim().length > 0);
  }

  public closeModalWithCancel(): Promise<boolean> {
    return this.modalCtrl.dismiss(null, 'cancel');
  }
}
